import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AdminRoute } from '@/components/AdminRoute';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { queryClient } from '@/lib/query-client';
import './index.css';

// Lazy-loaded pages
const SignInPage = lazy(() => import('@/pages/SignIn').then((m) => ({ default: m.SignInPage })));
const RegisterPage = lazy(() => import('@/pages/Register').then((m) => ({ default: m.RegisterPage })));
const DashboardPage = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })));
const EnrichmentsPage = lazy(() => import('@/pages/Enrichments').then((m) => ({ default: m.EnrichmentsPage })));
const ResultsPage = lazy(() => import('@/pages/Results').then((m) => ({ default: m.ResultsPage })));
const CreditsPage = lazy(() => import('@/pages/Credits').then((m) => ({ default: m.CreditsPage })));
const WebhookLogsPage = lazy(() => import('@/pages/WebhookLogs').then((m) => ({ default: m.WebhookLogsPage })));
const SettingsPage = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })));
const ProfilePage = lazy(() => import('@/pages/Profile').then((m) => ({ default: m.ProfilePage })));
const HelpFaqPage = lazy(() => import('@/pages/HelpFaq').then((m) => ({ default: m.HelpFaqPage })));
const SetupPage = lazy(() => import('@/pages/Setup').then((m) => ({ default: m.SetupPage })));
const OwnDataPage = lazy(() => import('@/pages/OwnData').then((m) => ({ default: m.OwnDataPage })));
const OwnDataAddTablePage = lazy(() => import('@/pages/OwnDataAddTable').then((m) => ({ default: m.OwnDataAddTablePage })));
const SetupEmailDomainPage = lazy(() => import('@/pages/SetupEmailDomain'));
const SetupLocationPage = lazy(() => import('@/pages/SetupLocation'));
const OwnDataWebhookPage = lazy(() => import('@/pages/OwnDataWebhook').then((m) => ({ default: m.OwnDataWebhookPage })));
const SetupHubPage = lazy(() => import('@/pages/SetupHub').then((m) => ({ default: m.SetupHubPage })));
const BookPage = lazy(() => import('@/pages/Book').then((m) => ({ default: m.BookPage })));
const Setup2FAPage = lazy(() => import('@/pages/Setup2FA').then((m) => ({ default: m.Setup2FAPage })));
const VerifyTotpPage = lazy(() => import('@/pages/VerifyTotp').then((m) => ({ default: m.VerifyTotpPage })));
const VerifyPage = lazy(() => import('@/pages/Verify').then((m) => ({ default: m.VerifyPage })));
const ForgotPasswordPage = lazy(() =>
  import('@/pages/ForgotPassword').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/ResetPassword').then((m) => ({ default: m.ResetPasswordPage })),
);
const LegalDocPage = lazy(() => import('@/pages/LegalDoc').then((m) => ({ default: m.LegalDocPage })));
const SubProcessorsPage = lazy(() =>
  import('@/pages/SubProcessors').then((m) => ({ default: m.SubProcessorsPage })),
);
const StudioPage = lazy(() => import('@/pages/Studio').then((m) => ({ default: m.StudioPage })));
const StudioProjectPage = lazy(() =>
  import('@/pages/StudioProject').then((m) => ({ default: m.StudioProjectPage })),
);

const AutomationsPage = lazy(() => import('@/pages/Automations').then((m) => ({ default: m.AutomationsPage })));
const AutomationDetailPage = lazy(() =>
  import('@/pages/AutomationDetail').then((m) => ({ default: m.AutomationDetailPage })),
);
const AdminAutomationsPage = lazy(() =>
  import('@/pages/admin/AdminAutomations').then((m) => ({ default: m.AdminAutomationsPage })),
);
const AdminAutomationEditPage = lazy(() =>
  import('@/pages/admin/AdminAutomationEditor').then((m) => ({ default: m.AdminAutomationEditorPage })),
);
const AdminLogsPage = lazy(() => import('@/pages/admin/AdminLogs').then((m) => ({ default: m.AdminLogsPage })));
const AdminApiPage = lazy(() => import('@/pages/admin/AdminApi').then((m) => ({ default: m.AdminApiPage })));
const AdminSettingsPage = lazy(() =>
  import('@/pages/admin/AdminSettings').then((m) => ({ default: m.AdminSettingsPage })),
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/setup-2fa" element={<Setup2FAPage />} />
            <Route path="/verify-totp" element={<VerifyTotpPage />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Public legal documents (no login required) */}
            <Route path="/terms" element={<LegalDocPage docKey="terms" />} />
            <Route path="/privacy" element={<LegalDocPage docKey="privacy" />} />
            <Route path="/dpa" element={<LegalDocPage docKey="dpa" />} />
            <Route path="/avv" element={<Navigate to="/dpa" replace />} />
            <Route path="/sub-processors" element={<SubProcessorsPage />} />

            {/* Protected routes with dashboard layout */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/enrichments" element={<EnrichmentsPage />} />
              <Route path="/studio" element={<StudioPage />} />
              <Route path="/studio/:projectId" element={<StudioProjectPage />} />
              <Route path="/automations" element={<AutomationsPage />} />
              <Route path="/automations/:automationId" element={<AutomationDetailPage />} />
              <Route path="/admin" element={<Navigate to="/admin/automations" replace />} />
              <Route path="/admin/automations" element={<AdminRoute><AdminAutomationsPage /></AdminRoute>} />
              <Route
                path="/admin/automations/:automationId"
                element={<AdminRoute><AdminAutomationEditPage /></AdminRoute>}
              />
              <Route path="/admin/logs" element={<AdminRoute><AdminLogsPage /></AdminRoute>} />
              <Route path="/admin/api" element={<AdminRoute><AdminApiPage /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/credits" element={<CreditsPage />} />
              <Route path="/webhook-logs" element={<WebhookLogsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/help" element={<HelpFaqPage />} />
              <Route path="/setup" element={<SetupHubPage />} />
              <Route path="/setup/own-data" element={<OwnDataPage />} />
              <Route path="/setup/name-optimization" element={<SetupPage />} />
              <Route path="/setup/own-data/new" element={<OwnDataAddTablePage />} />
              <Route path="/setup/own-data/:lookupId/replace" element={<OwnDataAddTablePage />} />
              <Route path="/setup/own-data/:lookupId/webhook" element={<OwnDataWebhookPage />} />
              <Route path="/setup/email-domain" element={<SetupEmailDomainPage />} />
              <Route path="/setup/location" element={<SetupLocationPage />} />
              <Route path="/book" element={<BookPage />} />
            </Route>

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/exports" element={<Navigate to="/results" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
